import { useIntl } from 'react-intl';

import type { Maybe } from '@xingternal/crate-common-graphql-types';
import { Currency } from '@xingternal/crate-common-graphql-types';
import type { JobSearchQueryBodyFragment } from '@xingternal/crate-jobs-graphql';
import { formatSalary } from '@xingternal/crate-jobs-helpers';

const filterTypesField = {
  JobFilterRemoteOption: 'remoteOption',
  JobFilterCompany: 'company',
  JobFilterEmploymentType: 'employmentType',
  JobFilterCareerLevel: 'careerLevel',
  JobFilterDiscipline: 'discipline',
  JobFilterIndustry: 'industry',
  JobFilterBenefit: 'benefit',
  // TODO: add support for the split benefit filter when the Search is migrated to Crate
  // JobFilterBenefitEmployeePerk: 'benefitEmployeePerk',
  // JobFilterBenefitWorkingCulture: 'benefitWorkingCulture',
  JobFilterCountry: 'country',
  JobFilterCity: 'city',
} as const;

type Options = {
  location?: Maybe<string>;
  radius?: Maybe<number>;
};

export const useJoinFilters = (
  filterCollection: JobSearchQueryBodyFragment['filterCollection'],
  { location, radius }: Options = {}
): string => {
  const { formatNumber, formatMessage } = useIntl();

  if (!filterCollection) {
    return '';
  }

  let hasCompanyFilter = false;
  const translatedFilters = filterCollection
    .map((filter) => {
      if (!filter?.__typename) {
        return false;
      }

      if (filter.__typename === 'JobFilterCompany') {
        hasCompanyFilter = true;
        return false;
      }

      if (
        filter.__typename === 'JobFilterSalary' &&
        typeof filter.min === 'number' &&
        typeof filter.max === 'number'
      ) {
        return formatSalary(
          formatNumber,
          [filter.min, filter.max],
          Currency.Eur
        );
      }

      if ('entityId' in filter) {
        const filterField = filterTypesField[filter.__typename];
        // @ts-expect-error: The only way to solve this issue is to use a switch or a type guard for each filter type
        return filter[filterField]?.localizationValue ?? false;
      }
    })
    .filter(Boolean);

  const filtersArray = [
    location,
    radius ? `${radius}km` : undefined,
    ...(hasCompanyFilter
      ? [formatMessage({ id: 'JOBS_SEARCH_COMPANY_CULTURE_FILTER_VALUE' })]
      : []),
    ...translatedFilters,
  ];

  return filtersArray.filter(Boolean).join(' + ');
};
